import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GridFilterModel } from '@mui/x-data-grid/models/gridFilterModel';
import { GridFilterItem, GridLinkOperator } from '@mui/x-data-grid/models/gridFilterItem';
import { GridEnrichedColDef } from '@mui/x-data-grid';
import { GenericModelList, GenericRelatedModelList, Item, SumRowsType } from 'drf-react-by-schema';
import { Link, useSearchParams } from 'react-router-dom';

import Box from '@mui/material/Box';

import { Layout } from 'src/styles';
import { MesocarpoProcessingStatus, ProcessingKey } from 'src/constants';
import { useAppSelector } from 'src/app/hooks';

import StatusChip from 'src/components/StatusChip';
import { ListHolder } from 'src/styles/styledComponents';
import MesocarpoProcessingCard from 'src/components/MesocarpoProcessingCard';
import MesocarpoNoResultsCard from 'src/components/MesocarpoNoResultsCard';

export default function MesocarpoProcessingList() {
  const { perfil, isMobile } = useAppSelector((state) => state.page);
  const [filters, setFilters] = useState<GridFilterModel>();
  const [checkedStatus, setCheckedStatus] = useState<MesocarpoProcessingStatus>();

  const [URLSearchParams] = useSearchParams();
  const statusFilterFromURL = URLSearchParams.get('status') as MesocarpoProcessingStatus | null;

  const model = 'mesocarpo_processing';
  const label = 'Beneficiamento de mesocarpo';

  const indexField = 'lote';

  const entrepostoId = useMemo(() => perfil?.entreposto?.id, [perfil?.entreposto]);

  const hiddenFields = undefined;

  const columnFields = useMemo(() => {
    const newColumnFields: string[] = ['lote'];
    if (!entrepostoId) {
      newColumnFields.push('entreposto', 'municipio');
    }
    newColumnFields.push(
      ...[
        'status',
        'lotes_producao',
        'data_processamento',
        'data_empacotamento',
        'peso_armazenado',
        'peso_processado',
        'pctes_um_quarto',
        'pctes_metade',
      ]
    );

    return newColumnFields;
  }, [entrepostoId]);

  const minWidthFields = useMemo(() => {
    const newMinwidthFields = {
      data_processamento: 100,
      ...(entrepostoId && {
        municipio: 150,
      }),
      status: 140,
      lotes_producao: 300,
      data_empacotamento: 100,
      data_flocos_umido: 100,
    };
    return newMinwidthFields;
  }, [entrepostoId]);

  const customColumnOperations: (column: GridEnrichedColDef) => GridEnrichedColDef = useCallback(
    (column) => {
      if (column.field === 'status') {
        column.renderCell = (params: Item) => {
          return (
            <StatusChip
              type={ProcessingKey.MESOCARPO}
              status={params.row.status.value as MesocarpoProcessingStatus}
            />
          );
        };
      }
      return column;
    },
    []
  );

  const sumRows: SumRowsType | undefined = useMemo(() => {
    let prefix: string = '';
    switch (checkedStatus) {
      case MesocarpoProcessingStatus.IN_PROGRESS:
        prefix = 'Mesocarpo em beneficiamento: ';
        break;
      case MesocarpoProcessingStatus.COMPLETE:
        prefix = 'Total de mesocarpo beneficiado: ';
        break;
    }
    return {
      rows: [
        {
          field: 'peso_armazenado',
          prefix,
          suffix: ' Kg',
        },
        {
          field: 'peso_processado',
          prefix,
          suffix: ' Kg',
        },
      ],
      severity: 'info',
    };
  }, [checkedStatus]);

  const defaultFilter = useMemo(() => {
    if (!perfil) {
      return;
    }

    const emptyItems: GridFilterItem[] = checkedStatus
      ? [
          {
            columnField: 'status',
            value: checkedStatus,
            operatorValue: 'equals',
          },
        ]
      : [];

    const newDefaultFilter = {
      linkOperator: GridLinkOperator.And,
      items: [...emptyItems],
    };

    return newDefaultFilter;
  }, [perfil, checkedStatus]);

  const handleClickFilter = useCallback(
    (statusClicked: MesocarpoProcessingStatus) => {
      setCheckedStatus(statusClicked === checkedStatus ? undefined : statusClicked);
    },
    [checkedStatus]
  );

  useEffect(() => {
    setFilters(defaultFilter);
  }, [defaultFilter]);

  useEffect(() => {
    if (statusFilterFromURL) {
      setCheckedStatus(statusFilterFromURL);
    }
  }, [statusFilterFromURL]);

  if (!perfil || !filters) {
    return null;
  }

  return (
    <ListHolder isMobile={isMobile}>
      <Box sx={Layout.topBarOverListWithScrollX}>
        {Object.values(MesocarpoProcessingStatus).map((status) => (
          <StatusChip
            key={`filter_${status}`}
            type={ProcessingKey.MESOCARPO}
            variant={checkedStatus === status ? 'filled' : 'outlined'}
            onClick={() => {
              handleClickFilter(status);
            }}
            status={status}
            size="medium"
          />
        ))}
      </Box>
      {entrepostoId ? (
        <GenericRelatedModelList
          model="entreposto"
          id={entrepostoId}
          relatedModel={model}
          label={label}
          indexField={indexField}
          columnFields={columnFields}
          hiddenFields={hiddenFields}
          minWidthFields={minWidthFields}
          customColumnOperations={customColumnOperations}
          sumRows={sumRows}
          creatableFields={[]}
          paginationMode="server"
          indexFieldBasePath={`/${model}/view/`}
          LinkComponent={Link}
          MobileListRenderItem={(props) => MesocarpoProcessingCard({ ...props, entrepostoId })}
          MobileRenderNoResults={MesocarpoNoResultsCard}
          tableAutoHeight={true}
          noCardWrapper={true}
          defaultFilter={filters}
          hideQuickFilterBar={false}
        />
      ) : (
        <GenericModelList
          model={model}
          hasHeader={false}
          indexField={indexField}
          columnFields={columnFields}
          hiddenFields={hiddenFields}
          minWidthFields={minWidthFields}
          customColumnOperations={customColumnOperations}
          sumRows={sumRows}
          creatableFields={[]}
          paginationMode="server"
          indexFieldBasePath={`/${model}/view/`}
          LinkComponent={Link}
          MobileListRenderItem={(props) => MesocarpoProcessingCard({ ...props, entrepostoId })}
          MobileRenderNoResults={MesocarpoNoResultsCard}
          tableAutoHeight={false}
          defaultFilter={filters}
          hideQuickFilterBar={false}
        />
      )}
    </ListHolder>
  );
}
